/**
 * i18n — react-i18next initialization for veterans/shop.
 * Replaces next-intl after Vite migration (T3756).
 *
 * Loads JSON message files from messages/ directory.
 * Fallback language: en-US.
 *
 * No top-level await — compatible with es2020 build target.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

/**
 * Dynamically load messages for the given locale, falling back to en-US.
 * Called by lazy-loading components when switching locale at runtime.
 */
export const loadMessages = async (locale: string): Promise<Record<string, unknown>> => {
  try {
    const mod = await import(`../../messages/${locale}.json`)
    return mod.default as Record<string, unknown>
  } catch {
    const fallback = await import('../../messages/en-US.json')
    return fallback.default as Record<string, unknown>
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // en-US resources loaded synchronously as empty object; translations are
    // loaded lazily on first render via i18next backend / addResourceBundle.
    resources: {},
    fallbackLng: 'en-US',
    supportedLngs: ['en-US', 'es-419'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  })
  .then(() => {
    // Eagerly load en-US after init so the default locale renders without flash.
    return loadMessages('en-US').then((msgs) => {
      i18n.addResourceBundle('en-US', 'translation', msgs, true, true)
    })
  })
  .catch(() => {
    // non-fatal — app still renders, just without translations
  })

export { i18n }
